import {
  VERTEX_ID,
  ENTITY_TYPE_PARAMETER,
  OBJECT_ID_QUERY_PARAMETER,
  LOADING_OR_ERROR_CARD_DIMENSION_NUMBER,
} from "helpers/constants/miscellaneous";
import { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { addQueryParameter, copyToClipboard } from "helpers/utility/utilities";
import { setSelectedCarouselIndex } from "redux/Modals/actions";
import { useLocation, useNavigate, useParams } from "react-router";
import { useDispatch } from "react-redux";
import {
  clearImagePageParamsContext,
  fetchNavigationCluster,
  setDataPageScrollPosition,
  setClusterPageScrollPosition,
} from "redux/SingleDataset/actions";

import Body from "./Body";
import Carousel from "./Carousel";

import {
  MDOption,
  MetadataType,
  OnUpdateAssetData,
  HoveredAssetDataType,
  CardVariant,
} from "types";
import styles from "./style.module.scss";
import ScrollAwareHover from "views/components/ScrollAwareHover";
import CardHeader from "views/pages/DataPage/CardsView/DataList/EnrichmentCard/CardHeader";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
interface AssetPreviewProps {
  index: number;
  assetData: any;
  singleData: any;
  containerRef: any;
  isVertex: boolean;
  debugInfo?: string;
  clearAssetData: any;
  isSelected: boolean;
  showCarousel: boolean;
  mdOptions: MDOption[];
  titleIcon: JSX.Element;
  cardMDType: MetadataType;
  selectedLabels: string[];
  mdLabels: string[] | null;
  indexOfPointerOut: number;
  title: string | JSX.Element;
  selectedMdIcon: JSX.Element;
  uniqueClusterWrapperId: string;
  enrichmentBoundingBoxes?: number[][];
  hoveredAssetData: HoveredAssetDataType;

  onSetAssetPreviewVertex: () => void;
  onUpdateAssetData: OnUpdateAssetData;
  onViewClick: (type: MetadataType) => void;
  onMDLabelSelection: (label: string) => void;
  onViewAllClick: (type: MetadataType) => void;
  onSetAssetPreviewFlag: (value: boolean) => void;
}

const AssetPreviewCard = ({
  index,
  title,
  isVertex,
  mdLabels,
  assetData,
  debugInfo,
  titleIcon,
  mdOptions,
  cardMDType,
  singleData,
  isSelected,
  containerRef,
  showCarousel,
  selectedLabels,
  clearAssetData,
  selectedMdIcon,
  hoveredAssetData,
  indexOfPointerOut,
  uniqueClusterWrapperId,
  enrichmentBoundingBoxes,
  onViewClick,
  onViewAllClick,
  onUpdateAssetData,
  onMDLabelSelection,
  onSetAssetPreviewFlag,
  onSetAssetPreviewVertex,
}: AssetPreviewProps) => {
  const [assetImage, setAssetImage] = useState("");
  const [transistionEnded, setTransistionEnded] = useState(false);
  const [linkClicked, setLinkClicked] = useState(false);

  const { datasetId, enrichDatasetId, clusterId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const queryParamsFilters = useAllQueryParamsFilters();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const isDataPage = !!datasetId && !clusterId;
  const isClusterPage = !!datasetId && !!clusterId;
  const cardVariant: CardVariant = isVertex
    ? CardVariant.VERTEX
    : isSelected
    ? CardVariant.SELECTED
    : CardVariant.DEFAULT;

  const singleCardRef = document.getElementById(uniqueClusterWrapperId);
  const isVertexSelected = !!queryParamsFilters[VERTEX_ID];
  const videoSrc = assetData?.video_uri || null;

  const timestamp = assetData.frame_timestamp;
  const type = videoSrc ? "VIDEO" : assetData.type || "IMAGE";
  const showAssetPreview = Object.keys(assetData).length > 0;
  const previewCount = singleData.previews ? singleData.previews.length : 0;

  const boundingBoxes =
    enrichmentBoundingBoxes ||
    (assetData.boundingBox ? [assetData.boundingBox] : null);

  function getNewPositions() {
    const container = containerRef.current;
    const card = document.getElementById(uniqueClusterWrapperId);
    let x: "left" | "right" | "center" | "" = "";
    let y: "top" | "bottom" = "top";

    if (!container || !card) return { x, y };

    // Calculate dimensions
    const computedStyle = window.getComputedStyle(container);
    const paddingLeft = parseFloat(computedStyle.paddingLeft);
    const paddingRight = parseFloat(computedStyle.paddingRight);

    const totalContainerWidth =
      container.offsetWidth - (paddingLeft + paddingRight);
    const totalContainerHeight = container.scrollHeight;

    const cardWidth = card.offsetWidth;
    const cardHeight = card.offsetHeight;
    const columns = Math.floor(totalContainerWidth / cardWidth);
    const rows = Math.floor(totalContainerHeight / cardHeight);

    // Assign position based on column logic
    const dynamicIndex = isVertexSelected
      ? index === -2
        ? 0
        : index + 1
      : index;
    const columnIndex = dynamicIndex % columns;

    if (columnIndex === 0) {
      x = "left";
    } else if (columnIndex === columns - 1) {
      x = "right";
    } else {
      x = "center";
    }

    const totalCount = rows * columns;
    if (rows > 1 && totalCount - columns < index + 1) {
      y = "bottom";
    }

    return { x, y };
  }
  const { x: assetPositionX, y: assetPositionY } = getNewPositions();

  const assetPreviewClassNames = classNames(styles.assetPreviewWrapper, {
    [styles.assetPreview]: showAssetPreview,
    [styles.hiddenDiv]: !showAssetPreview,
    [styles.alignLeft]: assetPositionX === "left",
    [styles.alignRight]: assetPositionX === "right",
    [styles.alignCenter]: assetPositionX === "center",
    [styles.alignTop]: assetPositionY === "top",
    [styles.alignBottom]: assetPositionY === "bottom",
    [styles.assetPreviewForSimilarity]: isVertex,
    [styles.assetPreviewForExport]: isSelected && !isVertex,
  });

  const handleLinkClick = () => {
    if (linkClicked) return;
    else setLinkClicked(true);

    const entityParam = searchParams.has(ENTITY_TYPE_PARAMETER)
      ? `?${ENTITY_TYPE_PARAMETER}=${searchParams.get(ENTITY_TYPE_PARAMETER)}`
      : "";

    const dynamicID =
      assetData.type === "IMAGE" ? assetData.media_id : assetData.image_id;

    let imagePath =
      location.pathname.split("/cluster")[0] +
      `/image/${dynamicID}${entityParam}`;

    if (assetData.type === "OBJECT") {
      imagePath = addQueryParameter(
        imagePath,
        OBJECT_ID_QUERY_PARAMETER,
        assetData.media_id
      );
    }

    const url = window.location.host + imagePath;

    copyToClipboard(url.toString());
  };

  const handleMouseLeave = () => {
    if (indexOfPointerOut > -1) return;
    setTransistionEnded(false);
    clearAssetData();
    onSetAssetPreviewFlag(false);
  };

  const assetPreviewStyle = () => {
    if (hoveredAssetData.id === assetData.media_id) {
      if (singleCardRef && hoveredAssetData && hoveredAssetData.dimensions) {
        //deducting the extra header height
        let height = singleCardRef.offsetHeight - 71;
        let width = singleCardRef.offsetWidth;
        const maxHeight = height * 2;
        const maxWidth = width * 2;
        const contentHeight = hoveredAssetData.dimensions.naturalHeight;
        const contentWidth = hoveredAssetData.dimensions.naturalWidth;

        // if height and width are -999(give card height and width)
        if (
          contentHeight === LOADING_OR_ERROR_CARD_DIMENSION_NUMBER &&
          contentWidth === LOADING_OR_ERROR_CARD_DIMENSION_NUMBER
        ) {
          return {
            height: `${singleCardRef.offsetHeight * 1.5}px`,
            width: `${singleCardRef.offsetWidth * 1.5}px`,
          };
        } else {
          const aspectRatio = contentWidth / contentHeight;

          if (aspectRatio > 1) {
            // Landscape orientation
            width = maxWidth;
            height = width / aspectRatio;
          } else {
            // Portrait or square orientation
            height = maxHeight;
            width = height * aspectRatio;
          }
        }
        return { height: `${height + 71}px`, width: `${width}px` };
      }
    }
    return undefined;
  };

  const renderCarousel = () => {
    if (showCarousel) {
      const imageIndex = singleData.previews.findIndex(
        (p: any) => p.media_id === assetData.media_id
      );

      return (
        <Carousel
          imageIndex={imageIndex}
          previews={singleData.previews}
          onUpdateAssetData={onUpdateAssetData}
        />
      );
    }

    return null;
  };

  const handleAssetPreviewDetailClick = (e: any) => {
    if (e.target?.classList?.toString()?.includes("noDetailsClick")) {
      return;
    }

    if (enrichDatasetId) {
      return;
    }
    clearAssetData();
    dispatch(clearImagePageParamsContext());

    const dynamicID =
      assetData.type === "IMAGE" ? assetData.media_id : assetData.image_id;

    const entityParam = searchParams.has(ENTITY_TYPE_PARAMETER)
      ? `?${ENTITY_TYPE_PARAMETER}=${searchParams.get(ENTITY_TYPE_PARAMETER)}`
      : "";

    let imagePath =
      location.pathname.split("/cluster")[0] +
      `/image/${dynamicID}${entityParam}`;

    if (assetData.type === "OBJECT") {
      imagePath = addQueryParameter(
        imagePath,
        OBJECT_ID_QUERY_PARAMETER,
        assetData.media_id
      );
    }

    if (isDataPage) {
      dispatch(
        fetchNavigationCluster(
          datasetId!,
          singleData.cluster_id,
          queryParamsFilters
        ) as any
      );
      dispatch(setDataPageScrollPosition(index));
    }

    if (isClusterPage) {
      dispatch(setClusterPageScrollPosition(index));
    }

    dispatch(setSelectedCarouselIndex(index));
    navigate(imagePath);
  };

  useEffect(() => {
    if (showAssetPreview) {
      switch (assetData.type) {
        case "IMAGE":
          setAssetImage(assetData.media_uri);
          break;
        case "OBJECT":
          setAssetImage(assetData.media_uri);
          break;
        default:
          return;
      }
    }

    return () => {
      setLinkClicked(false);
    };
  }, [showAssetPreview, assetData]);

  return (
    <ScrollAwareHover
      style={assetPreviewStyle()}
      onMouseLeave={handleMouseLeave}
      className={assetPreviewClassNames}
      onMouseOver={() => {}}
      onClick={(e) => e.stopPropagation()}
      onTransitionEnd={() => showAssetPreview && setTransistionEnded(true)}
    >
      <>
        <CardHeader
          index={index}
          title={title}
          mdLabels={mdLabels}
          mdOptions={mdOptions}
          titleIcon={titleIcon}
          debugInfo={debugInfo}
          cardMdType={cardMDType}
          cardVariant={cardVariant}
          selectedLabels={selectedLabels}
          selectedMdIcon={selectedMdIcon}
          onViewClick={onViewClick}
          onViewAllClick={onViewAllClick}
          onMDLabelSelection={onMDLabelSelection}
        />
        <Body
          type={type}
          videoSrc={videoSrc}
          imageSrc={assetImage}
          timestamp={timestamp}
          linkClicked={linkClicked}
          mdLabels={mdLabels || []}
          previewCount={previewCount}
          showSimilarityButton={!isVertex}
          hoveredAssetData={hoveredAssetData}
          boundingBoxes={transistionEnded ? boundingBoxes : null}
          onLinkClick={handleLinkClick}
          onSimilarityClick={onSetAssetPreviewVertex}
          onDetailsClick={handleAssetPreviewDetailClick}
        />
        {renderCarousel()}
      </>
    </ScrollAwareHover>
  );
};

export default AssetPreviewCard;
